/**
 * Playbook Matching API service (Plan 110).
 */

import { apiClient } from './client';
import type { MatchResponse, MatchDetail, CompareResult, DraftResult } from '../../types/playbook';

const BASE = '/api/v1/playbook-match';

export const playbookMatchApi = {
  // Matching
  runMatch: (documentClauseId: string, matterId: string) =>
    apiClient.post<MatchResponse>(`${BASE}/match`, {
      document_clause_id: documentClauseId,
      matter_id: matterId,
    }),

  confirmMatch: (data: {
    document_clause_id: string;
    playbook_clause_id: string;
    playbook_id: string;
    matter_id: string;
    confidence_score: number;
    confidence_level: string;
  }) => apiClient.post<MatchDetail>(`${BASE}/confirm`, data),

  getSelectedMatch: (clauseId: string, matterId: string) =>
    apiClient.get<MatchDetail | null>(`${BASE}/clause/${clauseId}`, {
      params: { matter_id: matterId },
    }),

  /** Batch: get all selected matches for a matter (single query). */
  getAllSelectedMatches: (matterId: string) =>
    apiClient.get<MatchDetail[]>(`${BASE}/matter/${matterId}/selected-matches`),

  getCandidates: (clauseId: string, matterId: string) =>
    apiClient.get<MatchDetail[]>(`${BASE}/clause/${clauseId}/candidates`, {
      params: { matter_id: matterId },
    }),

  deleteMatch: (matchId: string) =>
    apiClient.delete(`${BASE}/${matchId}`),

  setReviewed: (matchId: string, reviewed: boolean) =>
    apiClient.patch(`${BASE}/${matchId}/reviewed`, { reviewed }),

  // Actions
  compare: (matchId: string, positionType = 'house') =>
    apiClient.post<CompareResult>(`${BASE}/compare`, {
      match_id: matchId,
      position_type: positionType,
    }),

  modify: (matchId: string, positionType = 'house', userInstructions?: string) =>
    apiClient.post<DraftResult>(`${BASE}/modify`, {
      match_id: matchId,
      position_type: positionType,
      ...(userInstructions ? { user_instructions: userInstructions } : {}),
    }),

  replace: (matchId: string, positionType = 'house', userInstructions?: string) =>
    apiClient.post<DraftResult>(`${BASE}/replace`, {
      match_id: matchId,
      position_type: positionType,
      user_instructions: userInstructions,
    }),

  draftComment: (data: {
    match_id: string;
    position_type?: string;
    tone?: string;
    style?: string;
    redrafted_text?: string;
  }) => apiClient.post<DraftResult>(`${BASE}/draft-comment`, data),

  // Glyphe Assist: generate explanation / comment (routes through audit trail)
  generateExplanation: (matchId: string) =>
    apiClient.post<DraftResult>(`${BASE}/generate-explanation`, { match_id: matchId }),

  generateComment: (matchId: string) =>
    apiClient.post<DraftResult>(`${BASE}/generate-comment`, { match_id: matchId }),

  // Primary playbook
  setPrimary: (matterId: string, playbookId: string, isPrimary: boolean) =>
    apiClient.patch(`${BASE}/matters/${matterId}/playbooks/${playbookId}/primary`, {
      is_primary: isPrimary,
    }),
};
